import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromSurveyActions from '../actions/surveys.actions';
import { SURVEYS_FEATURE_KEY } from '../constants/state-keys';
import { ISurveysFeatureState } from '../interfaces/states/surveys-feature-state.interface';
import { Survey } from '../interfaces/survey.interface';

export function selectSurveyId(a: Survey): string {
    return a.id;
}

export const adapter: EntityAdapter<Survey> = createEntityAdapter<Survey>({
    selectId: selectSurveyId,
});

export const selectFeature = createFeatureSelector<ISurveysFeatureState>(SURVEYS_FEATURE_KEY);

export const { selectAll } = adapter.getSelectors();

export const selectAllSurveys = createSelector(selectFeature, selectAll);

export const getActiveSurvey = (state: ISurveysFeatureState) => state.activeSurvey;

export const selectActiveSurvey = createSelector(selectFeature, getActiveSurvey);

export const getEligibleUsersSurveyStatus = (state: ISurveysFeatureState) => state.eligibleUsersSurveyStatus;

export const selectEligibleUsersSurveyStatus = createSelector(selectFeature, getEligibleUsersSurveyStatus);

export const getUserSurveyStatus = (state: ISurveysFeatureState) => state.surveyStatus;

export const selectUserSurveyStatus = createSelector(selectFeature, getUserSurveyStatus);

export const getSurveysList = (state: ISurveysFeatureState) => state.surveysList;

export const selectSurveysList = createSelector(selectFeature, getSurveysList);

export const getOpenedSurvey = (state: ISurveysFeatureState) => state.openedSurvey;

export const selectOpenedSurvey = createSelector(selectFeature, getOpenedSurvey);

export const getIsVisible = (state: ISurveysFeatureState) => state.isPopupVisible;

export const selectIsVisible = createSelector(selectFeature, getIsVisible);

export const getCurrentQuestion = (state: ISurveysFeatureState) => state.currentQuestion;

export const getAnswers = (state: ISurveysFeatureState) => state.responses;

export const selectCurrentQuestion = createSelector(selectFeature, getCurrentQuestion);
export const selectCurrentAnswers = createSelector(selectFeature, getAnswers);

export const getOpenedSurveySummary = (state: ISurveysFeatureState) => state.openedSurveySummary;

export const selectOpenedSurveySummary = createSelector(selectFeature, getOpenedSurveySummary);

export const selectSurveysState = createFeatureSelector<ISurveysFeatureState>(SURVEYS_FEATURE_KEY);

const initialState: ISurveysFeatureState = adapter.getInitialState({
    activeSurvey: null,
    surveysList: [],
    openedSurveySummary: null,
    currentQuestion: 1,
    responses: {},
    surveyStatus: null,
    isPopupVisible: false,
    error: null,
    loading: false,
    openedSurvey: null,
    eligibleUsersSurveyStatus: [],
});

export const surveysReducer = createReducer(
    initialState,

    on(fromSurveyActions.createSurvey.createSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.createSurvey.createSurveySuccess, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.createSurvey.createSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.getActiveSurvey.getActiveSurvey, (state) => ({
        ...state,
        loading: true,
        error: undefined,
    })),
    on(fromSurveyActions.getActiveSurvey.getActiveSurveySuccess, (state, action) => ({
        ...state,
        activeSurvey: action.payload,
        loading: false,
    })),
    on(fromSurveyActions.getActiveSurvey.getActiveSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.getAllSurveys.getAllSurveys, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromSurveyActions.getAllSurveys.getAllSurveysSuccess, (state, action) => ({
        ...state,
        surveysList: action.payload,
    })),
    on(fromSurveyActions.getAllSurveys.getAllSurveysFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.getSurveyById.getSurveyById, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromSurveyActions.getSurveyById.getSurveyByIdSuccess, (state, action) => ({
        ...state,
        openedSurvey: action.payload,
        loading: false,
    })),
    on(fromSurveyActions.getSurveyById.getSurveyByIdFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatus, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromSurveyActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatusSuccess, (state, action) => ({
        ...state,
        eligibleUsersSurveyStatus: action.payload,
        loading: false,
    })),
    on(fromSurveyActions.getEligibleUsersSurveyStatus.getEligibleUsersSurveyStatusFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.deleteSurvey.deleteSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.deleteSurvey.deleteSurveySuccess, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.deleteSurvey.deleteSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.stopSurvey.stopSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.stopSurvey.stopSurveySuccess, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.stopSurvey.stopSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.downloadSurveyCsv.downloadSurveyCsv, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.downloadSurveyCsv.downloadSurveyCsvSuccess, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.downloadSurveyCsv.downloadSurveyCsvFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.saveResponse.saveResponse, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.saveResponse.saveResponseSuccess, (state, { questionNumber, response }) => ({
        ...state,
        responses: {
            ...state.responses,
            [`question${questionNumber}`]: response,
        },
    })),
    on(fromSurveyActions.saveResponse.saveResponseFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.dismissSurvey.dismissSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.dismissSurvey.dismissSurveySuccess, (state) => ({
        ...state,
        isPopupVisible: false,
    })),
    on(fromSurveyActions.dismissSurvey.dismissSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.startSurvey.startSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.startSurvey.startSurveySuccess, (state) => ({
        ...state,
        isPopupVisible: true,
    })),
    on(fromSurveyActions.startSurvey.startSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.getUserSurveyStatus.getUserSurveyStatus, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.getUserSurveyStatus.getUserSurveyStatusSuccess, (state, action) => ({
        ...state,
        responses: {
            ...state.responses,
            question1: action.payload?.response?.responses?.question1,
            question2: action.payload?.response?.responses?.question2,
            question3: action.payload?.response?.responses?.question3,
            question4: action.payload?.response?.responses?.question4,
        },
        isPopupVisible: false,
        surveyStatus: action.payload,
    })),
    on(fromSurveyActions.getUserSurveyStatus.getUserSurveyStatusFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.completeSurvey.completeSurvey, (state) => ({
        ...state,
    })),
    on(fromSurveyActions.completeSurvey.completeSurveySuccess, (state) => ({
        ...state,
        isPopupVisible: false,
    })),
    on(fromSurveyActions.completeSurvey.completeSurveyFailed, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromSurveyActions.showSurveyPopup.showSurveyPopup, (state) => ({
        ...state,
        isPopupVisible: true,
    })),

    on(fromSurveyActions.nextQuestion.nextQuestion, (state) => ({
        ...state,
        currentQuestion: state.currentQuestion < 5 ? state.currentQuestion + 1 : state.currentQuestion,
    })),

    on(fromSurveyActions.previousQuestion.previousQuestion, (state) => ({
        ...state,
        currentQuestion: state.currentQuestion < 5 ? state.currentQuestion - 1 : state.currentQuestion,
    })),

    on(fromSurveyActions.resetQuestionNumber, (state) => ({
        ...state,
        currentQuestion: 1,
    })),

    on(fromSurveyActions.getOpenedSurveySummary, (state, action) => ({
        ...state,
        openedSurveySummary: action.payload,
    })),
);
